import api from "../api";

class InitialInspectionReportsService {
  // Get form data for creating initial inspection report
  async getFormData(propertyId) {
    try {
      const response = await api.get(
        `/initialinspectionreports/properties/${propertyId}/form-data`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching initial inspection form data:", error);
      throw error;
    }
  }

  // Create initial inspection report
  async createInitialInspectionReport(propertyId, formData) {
    try {
      // Prepare data matching Laravel validation rules
      const reportData = {
        property_id: parseInt(propertyId),
        status_id: formData.status_id !== undefined ? formData.status_id : 1, // Default to 1 (active) only if undefined
        rooms_number:
          formData.rooms_number !== undefined ? formData.rooms_number : 0,

        // Basic property info
        client: formData.client || "",
        what_council: formData.what_council || "",
        building_type: formData.building_type || "",
        property_for_voa: formData.property_for_voa ? 1 : 0,
        floors: parseInt(formData.floors) || 0,
        toilets: parseInt(formData.toilets) || 0,
        bathrooms: parseInt(formData.bathrooms) || 0,
        kitchens: parseInt(formData.kitchens) || 0,
        parking: formData.parking ? 1 : 0,
        garden: formData.garden ? 1 : 0,

        // Rooms data
        rooms: formData.rooms || [],

        // Maintenance
        maintenance: formData.maintenance || "",

        // Installation counts
        installation_shower_cubicles:
          parseInt(formData.installation_shower_cubicles) || 0,
        installation_shower_electric_units:
          parseInt(formData.installation_shower_electric_units) || 0,
        installation_kitchens: parseInt(formData.installation_kitchens) || 0,
        installation_electric_heaters:
          parseInt(formData.installation_electric_heaters) || 0,
        installation_over_sink_water_heaters:
          parseInt(formData.installation_over_sink_water_heaters) || 0,
        installation_fridges: parseInt(formData.installation_fridges) || 0,
        installation_mini_ovens:
          parseInt(formData.installation_mini_ovens) || 0,
        installation_microwaves:
          parseInt(formData.installation_microwaves) || 0,
        installation_cookers: parseInt(formData.installation_cookers) || 0,

        // Locations
        locations_of_meters: formData.locations_of_meters || "",

        // S3 file keys (files already uploaded)
        image_gas_meters: formData.image_gas_meters || null,
        image_electric_meters: formData.image_electric_meters || null,
        image_water_meters: formData.image_water_meters || null,
        image_fuse_boards: formData.image_fuse_boards || null,

        // Rotation values from the lightbox. Backend rotates the S3 file in
        // place before inserting and strips these (they aren't DB columns).
        image_gas_meters_rotate: formData.image_gas_meters_rotate || null,
        image_electric_meters_rotate: formData.image_electric_meters_rotate || null,
        image_water_meters_rotate: formData.image_water_meters_rotate || null,
        image_fuse_boards_rotate: formData.image_fuse_boards_rotate || null,

        // Utility bills
        utility_bills_client: formData.utility_bills_client || "",
        utility_bills_ggm: formData.utility_bills_ggm || "",
        waste_collection_client: formData.waste_collection_client || "",
        waste_collection_ggm: formData.waste_collection_ggm || "",

        // Survey info
        surveyed_by: formData.surveyed_by || "",
        signed: formData.signed || "",
        report_date: formData.report_date || null,

        // Form info
        form: formData.form || "",
        issued: formData.issued || null,
        review_date: formData.review_date || null,
        approved: formData.approved || "",

        // Notes
        notes: formData.notes || "",
      };

      console.log("Sending report data:", reportData);

      // Create FormData for multipart/form-data submission (Laravel method)
      const formDataToSend = new FormData();

      // Append all form fields
      Object.keys(reportData).forEach((key) => {
        if (reportData[key] !== null && reportData[key] !== undefined) {
          if (key === "rooms" && Array.isArray(reportData[key])) {
            // Handle rooms array - send as indexed form fields (same as update)
            reportData[key].forEach((room, index) => {
              formDataToSend.append(`rooms[${index}]`, room);
            });
          } else if (Array.isArray(reportData[key])) {
            // For other arrays, send as JSON string
            formDataToSend.append(key, JSON.stringify(reportData[key]));
          } else {
            formDataToSend.append(key, reportData[key]);
          }
        }
      });

      // Append file objects if they exist
      const imageFields = [
        'image_gas_meters',
        'image_electric_meters',
        'image_water_meters',
        'image_fuse_boards'
      ];

      imageFields.forEach((fieldName) => {
        const fileKey = `${fieldName}_file`;
        if (formData[fileKey] && formData[fileKey] instanceof File) {
          formDataToSend.append(fieldName, formData[fileKey]);
          console.log(`Appending file for ${fieldName}:`, formData[fileKey].name);
        }
      });

      const response = await api.post(
        `/initialinspectionreports/properties/${propertyId}`,
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error creating initial inspection report:", error);
      throw error;
    }
  }

  // Get initial inspection report by ID
  async getInitialInspectionReport(id) {
    try {
      const response = await api.get(`/initialinspectionreports/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching initial inspection report:", error);
      throw error;
    }
  }

  // Update initial inspection report
  async updateInitialInspectionReport(id, formData) {
    try {
      // Create FormData for file uploads
      const formDataToSend = new FormData();

      // Add all form fields
      Object.keys(formData).forEach((key) => {
        if (key === "rooms" && Array.isArray(formData[key])) {
          // Handle rooms array
          formData[key].forEach((room, index) => {
            formDataToSend.append(`rooms[${index}]`, room);
          });
        } else if (formData[key] !== null && formData[key] !== undefined) {
          formDataToSend.append(key, formData[key]);
        }
      });

      const response = await api.put(
        `/initialinspectionreports/${id}`,
        formDataToSend,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating initial inspection report:", error);
      throw error;
    }
  }

  // Delete initial inspection report
  async deleteInitialInspectionReport(id) {
    try {
      const response = await api.delete(`/initialinspectionreports/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting initial inspection report:", error);
      throw error;
    }
  }

  // Download initial inspection report PDF (matches Laravel get method)
  async downloadInitialInspectionReport(propertyId, reportId) {
    try {
      const response = await api.get(
        `/initialinspectionreports/properties/${propertyId}/${reportId}`,
        {
          responseType: "blob",
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error downloading initial inspection report:", error);
      throw error;
    }
  }

  // Remove image from initial inspection report
  async removeImage(id, field) {
    try {
      const response = await api.delete(
        `/initialinspectionreports/${id}/images/${field}`
      );
      return response.data;
    } catch (error) {
      console.error("Error removing image:", error);
      throw error;
    }
  }

  // Get initial inspection reports for a property
  async getInitialInspectionReportsByProperty(propertyId) {
    try {
      const response = await api.get(
        `/initialinspectionreports/properties/${propertyId}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching initial inspection reports:", error);
      throw error;
    }
  }

  // Generic CRUD methods (keeping existing functionality)
  async create(data) {
    try {
      const response = await api.post("/initialinspectionreports", data);
      return response.data;
    } catch (error) {
      console.error("Error creating initial inspection report:", error);
      throw error;
    }
  }

  async getAll(params = {}) {
    try {
      const response = await api.get("/initialinspectionreports", { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching initial inspection reports:", error);
      throw error;
    }
  }

  async getByParam(params) {
    try {
      const response = await api.get("/initialinspectionreports/getByParam", {
        params,
      });
      return response.data;
    } catch (error) {
      console.error(
        "Error fetching initial inspection report by param:",
        error
      );
      throw error;
    }
  }

  async update(filter, data) {
    try {
      const response = await api.put("/initialinspectionreports", data, {
        params: filter,
      });
      return response.data;
    } catch (error) {
      console.error("Error updating initial inspection report:", error);
      throw error;
    }
  }

  async delete(filter) {
    try {
      const response = await api.delete("/initialinspectionreports", {
        params: filter,
      });
      return response.data;
    } catch (error) {
      console.error("Error deleting initial inspection report:", error);
      throw error;
    }
  }
}

export default new InitialInspectionReportsService();
